import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Busy, TypeChip } from "@/components/ui-bits";
import { api, errMsg, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Check, X } from "lucide-react";

export default function ProposalDialog({ proposal, onClose, onApplied }: { proposal: any; onClose: () => void; onApplied: () => void }) {
  const [busy, setBusy] = useState(false);
  if (!proposal) return null;
  const act = async (kind: "approve" | "dismiss") => {
    setBusy(true);
    try {
      await api.post(`/calendar/proposals/${proposal.id}/${kind}`);
      toast.success(kind === "approve" ? "Calendar updated" : "Proposal dismissed. Nothing changed.");
      onApplied();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const total = (proposal.added?.length || 0) + (proposal.moved?.length || 0) + (proposal.removed?.length || 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-cream max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="proposal-dialog">
        <DialogHeader>
          <p className="eyebrow">Proposed calendar changes</p>
          <DialogTitle className="font-serif text-2xl text-ink">Here's what I'd change, and why</DialogTitle>
          <DialogDescription className="text-ink-muted" data-testid="proposal-reason">{proposal.reason}</DialogDescription>
        </DialogHeader>
        {total === 0 && <p className="text-sm text-ink-muted" data-testid="proposal-empty">No changes needed. Your calendar already handles this.</p>}
        <Section title="Added" count={proposal.added?.length} tone="bg-olive-light text-olive-deep">
          {proposal.added?.map((a: any, i: number) => (
            <Row key={i} testId={`proposal-added-${i}`}>
              <div className="flex items-center gap-2 flex-wrap"><span className="text-xs font-semibold text-ink-muted w-24">{fmtDate(a.date)}</span><TypeChip type={a.type} /><span className="font-medium text-sm">{a.topic}</span></div>
              {a.why && <p className="text-xs text-ink-soft mt-1 ml-24">{a.why}</p>}
            </Row>
          ))}
        </Section>
        <Section title="Moved" count={proposal.moved?.length} tone="bg-[#E5E8A7] text-[#071D2B]">
          {proposal.moved?.map((m: any, i: number) => (
            <Row key={i} testId={`proposal-moved-${i}`}>
              <div className="flex items-center gap-2 text-sm"><span className="text-ink-muted">{fmtDate(m.from_date)}</span><ArrowRight className="size-3" /><span className="font-medium">{fmtDate(m.to_date)}</span></div>
              {m.why && <p className="text-xs text-ink-soft mt-1">{m.why}</p>}
            </Row>
          ))}
        </Section>
        <Section title="Removed" count={proposal.removed?.length} tone="bg-[#E5E8A7] text-[#687000]">
          {proposal.removed?.map((r: any, i: number) => (
            <Row key={i} testId={`proposal-removed-${i}`}><p className="text-sm font-medium line-through text-ink-muted">{r.topic}</p>{r.why && <p className="text-xs text-ink-soft mt-1">{r.why}</p>}</Row>
          ))}
        </Section>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
          <button className="btn-ghost" onClick={() => act("dismiss")} disabled={busy} data-testid="proposal-dismiss-button"><X className="size-4" /> Keep my calendar as is</button>
          <button className="btn-olive" onClick={() => act("approve")} disabled={busy || total === 0} data-testid="proposal-approve-button">{busy ? <Busy label="Applying..." /> : <><Check className="size-4" /> Apply these changes</>}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, count, tone, children }: any) => !count ? null : (
  <div className="mt-2">
    <div className="flex items-center gap-2 mb-2"><span className={`chip ${tone}`}>{title}</span><span className="text-xs text-ink-soft">{count}</span></div>
    <div className="divide-y divide-line border border-line rounded-xl">{children}</div>
  </div>
);
const Row = ({ children, testId }: any) => <div className="p-3" data-testid={testId}>{children}</div>;
